import { atom } from 'jotai';
import { TokenService } from '@/services/auth/tokenService';
import { CameraInfo, DPPodLKInfo, MicrophoneInfo } from '@/types/api/clients';
import { SensorPodInfo } from '../sensorpods';

// Stream action atoms - these are write-only atoms that trigger actions
export const startStreamAudioActionAtom = atom(
  null,
  async (
    _get,
    _set,
    {
      machineId,
      microphones,
    }: { machineId: string; microphones: MicrophoneInfo[] },
  ) => {
    const tokenService = TokenService.getInstance();
    const accessToken = await tokenService.getCurrentAccessToken();

    if (!accessToken) {
      throw new Error('Authentication required');
    }

    const topic = `stream/request/${machineId}`;
    interface MicrophonePayload {
      microphone_id: string;
      pod: string;
      sensor_pod: string;
      rtsp: string;
      whip: string;
      log_file: string;
    }
    const payload: { microphones: MicrophonePayload[] } = { microphones: [] };
    for (const microphone of microphones) {
      console.log(' starting microphone', microphone);
      payload.microphones.push({
        microphone_id: microphone.microphone_id,
        pod: microphone.dp_name,
        sensor_pod: microphone.pod_name,
        rtsp: microphone.rtsp,
        whip: microphone.whip,
        log_file: microphone.log_file,
      });
    }
    const message = {
      command: 'start_audio',
      payload: payload,
    };
    publishThruAPI(topic, message, accessToken);
  },
);

export const stopStreamAudioActionAtom = atom(
  null,
  async (_get, _set, { machineId }: { machineId: string }) => {
    const tokenService = TokenService.getInstance();
    const accessToken = await tokenService.getCurrentAccessToken();

    if (!accessToken) {
      throw new Error('Authentication required');
    }

    const topic = `stream/request/${machineId}`;
    const message = {
      command: 'stop_audio',
    };
    publishThruAPI(topic, message, accessToken);
  },
);

export const startStreamActionAtom = atom(
  null,
  async (
    _get,
    _set,
    { machineId, cameras }: { machineId: string; cameras: CameraInfo[] },
  ) => {
    const tokenService = TokenService.getInstance();
    const accessToken = await tokenService.getCurrentAccessToken();

    if (!accessToken) {
      throw new Error('Authentication required');
    }
    const topic = `stream/request/${machineId}`;
    const camera_data = [];
    for (const cam of cameras) {
      camera_data.push({
        rtsp: cam.rtsp,
        whip: cam.whip,
        log_file: cam.log_file,
      });
    }
    const message = {
      command: 'start_stream',
      payload: {
        cameras: camera_data,
      },
    };
    return publishThruAPI(topic, message, accessToken);
  },
);

export const stopStreamActionAtom = atom(
  null,
  async (_get, _set, { machineId }: { machineId: string }) => {
    const tokenService = TokenService.getInstance();
    const accessToken = await tokenService.getCurrentAccessToken();

    if (!accessToken) {
      throw new Error('Authentication required');
    }

    const topic = `stream/request/${machineId}`;
    const message = {
      command: 'stop_stream',
    };
    return publishThruAPI(topic, message, accessToken);
  },
);

export const startDPStreamActionAtom = atom(
  null,
  async (
    _get,
    _set,
    {
      machineId,
      sensorPods,
      deterrencePods,
    }: {
      machineId: string;
      sensorPods: SensorPodInfo[];
      deterrencePods: DPPodLKInfo[];
    },
  ) => {
    const tokenService = TokenService.getInstance();
    const accessToken = await tokenService.getCurrentAccessToken();

    if (!accessToken) {
      throw new Error('Authentication required');
    }

    const topic = `stream/request/${machineId}`;
    const camera_data = [];

    console.log('sensor_pods:', sensorPods);
    for (const cam of sensorPods) {
      camera_data.push({
        rtsp: cam.rtsp || '',
        whip: cam.whip || '',
        log_file: cam.log_file || '',
      });
    }

    console.log('deterrence_pods:', deterrencePods);
    for (const cam of deterrencePods) {
      camera_data.push({
        rtsp: cam.rtsp_video || '',
        whip: cam.whip_video || '',
        log_file: cam.log_file || '',
      });
    }

    const message = {
      command: 'start_dp_stream',
      payload: {
        cameras: camera_data,
      },
    };
    return publishThruAPI(topic, message, accessToken);
  },
);

export const publishThruAPI = async (
  topic: string,
  message: object,
  authToken: string,
) => {
  try {
    const request = {
      topic,
      message,
    };
    const response = await fetch(`${process.env.PUBLIC_PUBLISH_API_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(request),
    });
    console.log('Response:', response);
    if (!response.ok) {
      const errorData = await response.text();
      console.error('Error publishing message:', errorData);
      throw new Error('Failed to publish message');
    }
    const data = await response.json();
    console.log('Published message:', data);
    return data;
  } catch (error) {
    console.error('Error publishing message:', error);
    throw error;
  }
};
